import React, { MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import DocumentActionsDropdownContainer from './DocumentActionsDropdownContainer';
import { useModals } from '../../contexts/ModalsContext';

import { getDocumentStatusIndicator } from './helpers/documentStatusIndication';
import { DocumentStatusEnum } from '../../shared/constants/document';
import { ReactComponent as DangerIcon } from '../../shared/icons/alert-danger-icon.svg';
import { ReactComponent as DocumentCheckIcon } from '../../shared/icons/document-check-icon.svg';
import { ReactComponent as ChainBrokenIcon } from '../../shared/icons/chain-broken-icon.svg';
import { ExtractedTermsSlideout } from '../extractedTermsSlideout';
import { LinkAmendmentModal } from '../linkAmendmentModal';
import {
  ApprovalRequestThreshold,
  InternalApprovalReview,
} from '../document-wizard/document-wizard-header/components/headerActions/requestReview/RequestInternalApprovalModalConstants';
import Tooltip from '../tooltip';
import { InternalApprovalApproversTooltipTable } from '../document-wizard/document-wizard-header/components/headerActions/requestReview/InternalApprovalApproversTooltipTable';
import { approversCounter } from '../document-wizard/document-wizard-header/components/headerActions/requestReview/InternalApprovalBadgeContainer';
import {
  signersCounter,
  SignersTooltipTable,
} from '../document-wizard/document-wizard-header/components/headerActions/sendToPreparingToSignInternal/signingProcessBadgeContainer';
import { DocumentSigner } from '../../contexts/DocumentContext';
import { ColumnsCellType, DocumentTableColumnProps } from './helpers/documentTableTypes';
import styles from './DocumentTable.module.scss';
import { defineAlertableDocumentTooltipMessage } from './helpers/documentDefinitionHelpers';

export const DocumentTableColumns = ({
  alertableDocumentsIDs,
  teamId,
  dealId,
  handleEditDocument,
  isDealShared,
  permissions,
}: DocumentTableColumnProps) => [
  {
    Header: <FormattedMessage id="title.documents.name" defaultMessage=" Name" />,
    accessor: 'name',
    Cell: ({
      cell: {
        value,
        row: {
          values: { status, document },
        },
      },
    }: ColumnsCellType) => {
      const isDocumentAlertable = alertableDocumentsIDs?.includes(document.id);
      const tooltipMessage = defineAlertableDocumentTooltipMessage({ documentStatus: status });

      return (
        <span
          data-testid={'document-name-cell'}
          className={styles.documentNameCell}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {isDocumentAlertable && (
            <span className={styles.alertIcon}>
              <Tooltip
                testId="alertable-icon-tooltip"
                icon={<DangerIcon width={20} height={20} data-testid={'alertable-icon'} />}
                placement="bottom"
              >
                {tooltipMessage}
              </Tooltip>
            </span>
          )}
          <span className={styles.documentTitle}>
            <FormattedMessage id="cta.go-to.details" defaultMessage={value} />
          </span>
        </span>
      );
    },
  },
  {
    Header: <FormattedMessage id="title.deals.template-status" defaultMessage="Status" />,
    accessor: 'status',
    Cell: ({
      cell: {
        row: {
          values: { status, document },
        },
      },
    }: ColumnsCellType) => {
      const documentStatus = getDocumentStatusIndicator(status, document.currentTeamPossession);
      const approvalRequestedReviews = document.approvalRequest?.reviews;
      const isOneConfirmationRequired = document.approvalRequest?.threshold === ApprovalRequestThreshold.ONE;
      const approvedReviewsRequests =
        approvalRequestedReviews?.filter((request: InternalApprovalReview) => !!request.approvedAt).length || 0;
      const totalApprovalReviewRequests = approvalRequestedReviews?.length || 0;

      const totalSigners = document.signers?.length || 0;
      const hasSignedNumber = document.signers?.filter((signer: DocumentSigner) => !!signer.signedAt).length || 0;

      const statusForDocumentWithActiveInternalApproval = () => (
        <div>
          <span>
            <FormattedMessage id="document-internal-approval.badge" defaultMessage="Approval Pending" />
          </span>
          <span className="pl-2">
            <Tooltip
              testId="internal-approvers-counter"
              icon={approversCounter({
                approvedReviewsRequests,
                isOneConfirmationRequired,
                totalApprovalReviewRequests,
              })}
              placement="bottom-end"
            >
              {isOneConfirmationRequired && <p className={styles.internalApprovalTooltipTitle}>1 Approval Required</p>}
              <InternalApprovalApproversTooltipTable
                approvalRequestedReviews={approvalRequestedReviews}
                useApprovalOrder={Boolean(document.approvalRequest?.useApprovalOrder)}
                currentApproverId={(document.approvalRequest?.currentApprover as any)?.id}
                isCreator={false}
              />
            </Tooltip>
          </span>
        </div>
      );

      const statusForSigningDocument = () => (
        <div>
          <span>
            <FormattedMessage id="document-internal-approval.badge" defaultMessage="Signing" />
          </span>
          <span className="pl-2">
            <Tooltip
              testId="signers-approvers-counter"
              icon={signersCounter({
                hasSignedNumber,
                totalSigners,
              })}
              placement="bottom-end"
            >
              <SignersTooltipTable
                signers={document.signers || []}
                useSignerOrder={Boolean((document as any).useSignerOrder)}
              />
            </Tooltip>
          </span>
        </div>
      );

      return (
        <span className={styles.documentStatus} data-testid={'document-status-cell'}>
          {documentStatus === 'Approval Pending' ? (
            statusForDocumentWithActiveInternalApproval()
          ) : documentStatus === DocumentStatusEnum.SIGNING ? (
            statusForSigningDocument()
          ) : (
            <FormattedMessage id="phrase.template-status-readout" defaultMessage={documentStatus} values={{ status }} />
          )}
        </span>
      );
    },
  },
  {
    Header: <FormattedMessage id="title.documents.access" defaultMessage="Access" />,
    accessor: 'access',
    Cell: ({
      cell: {
        row: {
          values: { access },
        },
      },
    }: ColumnsCellType) => <span data-testid={'document-access-cell'}>{access}</span>,
  },
  {
    Header: (
      <span className="sr-only">
        <FormattedMessage id="title.deals.detailsLink" defaultMessage="Link to Deal Details" />
      </span>
    ),
    accessor: 'document',
    Cell: ({
      cell: {
        row: { values },
      },
    }: ColumnsCellType) => {
      const documentId = values.document.id;
      const hasCurrentTeamPossession = values.document.currentTeamPossession;
      const documentStatus = values.document.status;
      const isDocumentCreator = values.document.currentUserRole === 'creator';
      const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
      const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
      const { closeAllDropdowns } = useModals();
      const extractedTerm = (values.document as any).extractedDocumentTerm;
      const isUnlinkedAmendment = extractedTerm?.classification === 'amendment' && extractedTerm?.linkStatus === 'pending_link';

      // Don't show per-row loading spinner - causes multiple overlapping spinners
      // The dropdown will handle loading state gracefully
      return (
        <div
          onClick={(evt: MouseEvent) => {
            evt.stopPropagation();
          }}
          className={styles.documentDropdownContainer}
        >
          {extractedTerm && isDocumentCreator && (
            <>
              {isUnlinkedAmendment && (
                <>
                  <button
                    className={styles.chainBrokenIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeAllDropdowns();
                      setIsLinkModalOpen(true);
                    }}
                    data-tooltip-id={`chain-broken-tooltip-${documentId}`}
                    aria-label="Link amendment to base document"
                  >
                    <ChainBrokenIcon width={20} height={20} />
                  </button>
                  <ReactTooltip
                    id={`chain-broken-tooltip-${documentId}`}
                    place="bottom"
                    style={{
                      backgroundColor: 'rgba(221, 235, 239, 1)',
                      color: '#18191b',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      zIndex: 1000000,
                    }}
                  >
                    Link to Base Document
                  </ReactTooltip>
                </>
              )}
              <button
                className={styles.extractedDataIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  closeAllDropdowns();
                  setIsSlideoutOpen(true);
                }}
                data-tooltip-id={`extracted-data-tooltip-${documentId}`}
                aria-label="View extracted data"
              >
                <DocumentCheckIcon width={24} height={24} />
              </button>
              <ReactTooltip
                id={`extracted-data-tooltip-${documentId}`}
                place="bottom"
                style={{
                  backgroundColor: 'rgba(221, 235, 239, 1)',
                  color: '#18191b',
                  fontSize: '14px',
                  fontWeight: 'normal',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  zIndex: 1000000,
                }}
              >
                Extracted Data
              </ReactTooltip>
            </>
          )}
          <DocumentActionsDropdownContainer
            teamId={teamId}
            dealId={dealId}
            isDealShared={isDealShared}
            isDocumentCreator={isDocumentCreator}
            permissions={permissions}
            viewAction={() => handleEditDocument(documentId, hasCurrentTeamPossession, documentStatus)}
            documentData={values.document}
            dropdownId={documentId}
          />
          {extractedTerm && isDocumentCreator && (
            <>
              <ExtractedTermsSlideout
                isOpen={isSlideoutOpen}
                onClose={() => setIsSlideoutOpen(false)}
                documentId={documentId}
                documentName={values.document.title}
                extractedTerm={extractedTerm}
                canEdit={permissions.hasEditPermission}
                dealId={dealId}
                onLinkAmendment={isUnlinkedAmendment ? () => setIsLinkModalOpen(true) : undefined}
              />
              {isUnlinkedAmendment && (
                <LinkAmendmentModal
                  isOpen={isLinkModalOpen}
                  onClose={() => setIsLinkModalOpen(false)}
                  amendmentId={extractedTerm.id}
                  amendmentTitle={values.document.title}
                  dealId={dealId}
                  onSuccess={() => {
                    // Refresh the page to show updated data
                    window.location.reload();
                  }}
                />
              )}
            </>
          )}
        </div>
      );
    },
    disableSortBy: true,
  },
];
